import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Crown, Sparkles, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import NavigationMenu from '../components/NavigationMenu';
import { usePlatform } from '../hooks/useIAP';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isIOS, isWeb } = usePlatform();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [iapReady, setIapReady] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [iapStore, setIapStore] = useState(null);

  useEffect(() => {
    fetchSubscriptionStatus();
    
    // Inicializar IAP se estiver no iOS
    if (isIOS) {
      console.log('🔄 iOS detectado - carregando IAP...');
      initializeIAP();
    } else {
      console.log('🌐 Web detectado - IAP não necessário');
    }
  }, [isIOS]);

  const initializeIAP = async () => {
    try {
      console.log('📦 Verificando CdvPurchase global...');
      
      // Aguardar até que window.CdvPurchase esteja disponível
      let attempts = 0;
      while (!window.CdvPurchase && attempts < 20) {
        console.log(`⏳ Aguardando CdvPurchase... tentativa ${attempts + 1}`);
        await new Promise(resolve => setTimeout(resolve, 500));
        attempts++;
      }
      
      if (!window.CdvPurchase) {
        throw new Error('CdvPurchase não disponível após 10 segundos');
      }
      
      console.log('✅ window.CdvPurchase encontrado!');
      
      const { store, ProductType, Platform, LogLevel } = window.CdvPurchase;
      
      console.log('✅ Objetos obtidos:', {
        store: !!store,
        ProductType: !!ProductType,
        Platform: !!Platform
      });
      
      // Salvar store no state
      setIapStore(store);
      
      console.log('🔄 Configurando IAP Store...');
      
      // Configurar verbosity
      store.verbosity = LogLevel.DEBUG;
      
      // Registrar produtos
      console.log('📝 Registrando produtos...');
      store.register([
        {
          id: 'com.mairecipes.premium.individual',
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE
        },
        {
          id: 'com.mairecipes.premium.family',
          type: ProductType.PAID_SUBSCRIPTION,
          platform: Platform.APPLE_APPSTORE
        }
      ]);
      
      console.log('📦 2 produtos registrados');
      
      // Listener para quando store estiver pronto - CONFIGURAR TUDO AQUI
      try {
        console.log('📡 Configurando listener ready...');
        store.ready(() => {
          console.log('✅ Store READY callback disparado');
          
          // Configurar handlers DENTRO do ready (API correta)
          console.log('⚙️ Configurando handlers agora que store está pronto...');
          
          // Handler Premium - SEPARADOS (não em chain)
          store.when('com.mairecipes.premium.individual').approved((transaction) => {
            console.log('✅ Premium approved');
            transaction.verify();
          });
          
          store.when('com.mairecipes.premium.individual').verified(async (receipt) => {
            console.log('✅ Premium verified');
            await verifyReceiptOnBackend(receipt, 'com.mairecipes.premium.individual');
            receipt.finish();
            setUpgrading(false);
          });
          
          // Handler Family - SEPARADOS (não em chain)
          store.when('com.mairecipes.premium.family').approved((transaction) => {
            console.log('✅ Family approved');
            transaction.verify();
          });
          
          store.when('com.mairecipes.premium.family').verified(async (receipt) => {
            console.log('✅ Family verified');
            await verifyReceiptOnBackend(receipt, 'com.mairecipes.premium.family');
            receipt.finish();
            setUpgrading(false);
          });
          
          // Global error handler
          store.error((error) => {
            console.error('❌ Global IAP error:', error);
            toast.error(`Erro: ${error.message || 'Não foi possível processar a compra'}`);
            setUpgrading(false);
          });
          
          console.log('✅ Handlers configurados');
          
          // Verificar se produtos carregaram
          const premium = store.get('com.mairecipes.premium.individual');
          const family = store.get('com.mairecipes.premium.family');
          console.log('📋 Premium carregado?', !!premium, premium?.id);
          console.log('📋 Family carregado?', !!family, family?.id);
          
          if (premium && family) {
            console.log('✅ AMBOS produtos carregados!');
            setProductsLoaded(true);
          } else {
            console.warn('⚠️ Nem todos os produtos carregaram');
          }
        });
        console.log('✅ Listener ready configurado');
      } catch (readyError) {
        console.error('❌ Erro ao configurar ready:', readyError);
        throw readyError;
      }
      
      // Listener para produtos atualizados
      try {
        console.log('📡 Configurando listener updated...');
        store.when().updated((product) => {
          console.log('📦 Produto updated:', product.id);
        });
        console.log('✅ Listener updated configurado');
      } catch (updateError) {
        console.error('⚠️ Erro em updated (não crítico):', updateError);
      }
      
      // Inicializar store
      console.log('🚀 Inicializando store...');
      try {
        await store.initialize([Platform.APPLE_APPSTORE]);
        console.log('✅ Store.initialize() retornou');
      } catch (initError) {
        console.error('❌ Erro em initialize:', initError);
        throw initError;
      }
      
      console.log('✅ IAP setup completo');
      setIapReady(true);
      
    } catch (error) {
      console.error('❌ Erro ao inicializar IAP:', error);
      setIapReady(false);
      setProductsLoaded(false);
    }
  };

  const setupIAPHandlers = (store) => {
    // Handler para Premium
    store.when('com.mairecipes.premium.individual').approved((transaction) => {
      console.log('✅ Premium aprovado:', transaction);
      transaction.verify();
    });
    
    store.when('com.mairecipes.premium.individual').verified(async (receipt) => {
      console.log('✅ Premium verificado');
      await verifyReceiptOnBackend(receipt, 'com.mairecipes.premium.individual');
      receipt.finish();
      setUpgrading(false);
    });
    
    store.when('com.mairecipes.premium.individual').error((error) => {
      console.error('❌ Erro Premium:', error);
      toast.error(`Erro: ${error.message || 'Não foi possível processar a compra'}`);
      setUpgrading(false);
    });
    
    // Handler para Family
    store.when('com.mairecipes.premium.family').approved((transaction) => {
      console.log('✅ Family aprovado:', transaction);
      transaction.verify();
    });
    
    store.when('com.mairecipes.premium.family').verified(async (receipt) => {
      console.log('✅ Family verificado');
      await verifyReceiptOnBackend(receipt, 'com.mairecipes.premium.family');
      receipt.finish();
      setUpgrading(false);
    });
    
    store.when('com.mairecipes.premium.family').error((error) => {
      console.error('❌ Erro Family:', error);
      toast.error(`Erro: ${error.message || 'Não foi possível processar a compra'}`);
      setUpgrading(false);
    });
    
    console.log('✅ Handlers configurados para ambos produtos');
  };

  const verifyReceiptOnBackend = async (receipt, productId) => {
    try {
      await api.post('/iap/apple/verify-receipt', {
        receipt: receipt.nativeData?.appStoreReceipt || receipt.data,
        product_id: productId
      });
      
      toast.success(t('subscription.subscriptionActivated'));
      await fetchSubscriptionStatus();
    } catch (error) {
      console.error('Erro ao verificar no backend:', error);
      toast.error('Erro ao verificar compra. Contate o suporte.');
    }
  };

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscription(response.data.subscription);
      setUsage(response.data.usage);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setUpgrading(true);
    
    // Se for iOS, DEVE usar IAP (exigência da Apple)
    if (isIOS) {
      console.log('📱 iOS - iniciando compra IAP');
      
      // VERIFICAÇÃO CRÍTICA: IAP está pronto?
      if (!iapStore || !iapReady) {
        console.error('❌ IAP não está pronto');
        toast.error('A loja ainda está carregando. Por favor, aguarde alguns segundos.');
        setUpgrading(false);
        return;
      }
      
      // VERIFICAÇÃO CRÍTICA: Produtos carregaram?
      if (!productsLoaded) {
        console.error('❌ Produtos não carregaram');
        toast.error('Produtos não disponíveis. Verifique sua conexão e tente novamente.');
        setUpgrading(false);
        return;
      }
      
      try {
        const product = iapStore.get('com.mairecipes.premium.individual');
        
        // VERIFICAÇÃO CRÍTICA: Produto existe?
        if (!product) {
          console.error('❌ Produto Premium não encontrado na lista');
          console.log('📋 Produtos disponíveis:', availableProducts.map(p => p.id));
          toast.error('Produto Premium não está disponível. Entre em contato com o suporte.');
          setUpgrading(false);
          return;
        }
        
        console.log('🛒 Produto Premium encontrado:', product.id);
        console.log('📦 Status do produto:', {
          canPurchase: product.canPurchase,
          owned: product.owned,
          state: product.state
        });
        
        // VERIFICAÇÃO CRÍTICA: Pode comprar?
        if (!product.canPurchase) {
          console.error('❌ Produto não pode ser comprado');
          toast.error('Este produto não está disponível para compra no momento.');
          setUpgrading(false);
          return;
        }
        
        // TUDO OK - Iniciar compra
        console.log('💳 Iniciando ordem de compra...');
        
        // Obter o offer (não o produto diretamente)
        const offer = product.getOffer();
        if (!offer) {
          console.error('❌ Nenhum offer disponível para este produto');
          toast.error('Oferta não disponível. Entre em contato com o suporte.');
          setUpgrading(false);
          return;
        }
        
        console.log('💰 Offer encontrado:', offer.id, offer);
        
        // Order com o offer (API correta)
        iapStore.order(offer);
        console.log('✅ Ordem iniciada - aguardando resposta da Apple');
        
        // O handler vai processar quando aprovado
        // setUpgrading(false) será chamado no handler
        
      } catch (error) {
        console.error('❌ Erro ao comprar:', error);
        toast.error(`Erro: ${error.message || 'Não foi possível iniciar a compra'}`);
        setUpgrading(false);
      }
      
    } else {
      // Web - usar Stripe
      try {
        const originUrl = window.location.origin;
        const response = await api.post('/subscription/checkout', {
          plan: 'premium',
          origin_url: originUrl
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error('Erro Stripe:', error);
        toast.error(t('common.error'));
        setUpgrading(false);
      }
    }
  };

  const handleUpgradeFamily = async () => {
    setUpgrading(true);
    try {
      // Se for iOS, usar Apple IAP
      if (isIOS) {
        const { InAppPurchase2 } = await import('@awesome-cordova-plugins/in-app-purchase-2');
        const store = InAppPurchase2.store;
        
        const productId = 'com.mairecipes.premium.family';
        
        // Registrar produto
        store.register({
          id: productId,
          type: store.PAID_SUBSCRIPTION
        });
        
        // Handler de aprovação
        store.when(productId).approved(async (product) => {
          console.log('✅ Compra aprovada:', product);
          
          // Verificar recibo no backend
          try {
            await api.post('/iap/apple/verify-receipt', {
              receipt: product.transaction.appStoreReceipt,
              product_id: productId
            });
            
            toast.success(t('subscription.subscriptionActivated'));
            await fetchSubscriptionStatus();
            product.finish();
          } catch (error) {
            console.error('Erro ao verificar recibo:', error);
            toast.error(t('common.error'));
            product.finish();
          }
          
          setUpgrading(false);
        });
        
        // Handler de erro
        store.when(productId).error((error) => {
          console.error('❌ Erro na compra:', error);
          toast.error(t('common.error'));
          setUpgrading(false);
        });
        
        // Iniciar compra
        store.order(productId);
        store.refresh();
        
      } else {
        // Se for Web, usar Stripe
        const originUrl = window.location.origin;
        const response = await api.post('/subscription/checkout', {
          plan: 'family',
          origin_url: originUrl
        });
        
        if (response.data.url) {
          window.location.href = response.data.url;
        }
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('common.error'));
      setUpgrading(false);
    }
  };

  const isPremium = subscription?.plan === 'premium' && 
    (subscription?.status === 'trial' || subscription?.status === 'active');
  
  const isFree = !subscription || subscription?.plan === 'free';

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center items-center gap-3 mb-4">
            <Crown className="w-12 h-12 text-orange-600" />
          </div>
          <h1 className="text-5xl font-bold text-amber-900 mb-4">{t('subscription.choosePlan')}</h1>
          <p className="text-xl text-gray-600">{t('subscription.unlockPotential')}</p>
        </div>

        {/* Current Status */}
        {!loading && subscription && (
          <Card className="max-w-2xl mx-auto p-6 mb-8 border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('subscription.currentPlan')}</h3>
              <div className="flex items-center justify-center gap-2 mb-4">
                <Badge className={`text-white text-lg px-4 py-2 ${
                  subscription.plan === 'family' ? 'bg-green-600' : 
                  subscription.plan === 'premium' ? 'bg-orange-600' : 
                  'bg-gray-600'
                }`}>
                  {subscription.plan === 'family' ? `👨‍👩‍👧‍👦 ${t('subscription.family')}` : 
                   subscription.plan === 'premium' ? `👑 ${t('subscription.premium')}` : 
                   `🆓 ${t('subscription.free')}`}
                </Badge>
                {subscription.status === 'trial' && (
                  <Badge variant="secondary">{t('subscription.trialActive')}</Badge>
                )}
              </div>
              
              {usage && subscription.plan === 'free' && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{usage.recipes_this_month}/5</div>
                    <div className="text-xs text-gray-600">{t('limits.recipesThisMonth')}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{usage.ai_generations_this_week}/3</div>
                    <div className="text-xs text-gray-600">{t('limits.aiThisWeek')}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{usage.families}/1</div>
                    <div className="text-xs text-gray-600">{t('subscription.families')}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {/* Free Plan */}
          <Card className="p-8 border-2 border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('subscription.free')}</h3>
              <div className="text-4xl font-bold text-gray-900 mb-1">$0</div>
              <div className="text-gray-600">{t('subscription.forever')}</div>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-gray-700">{t('subscription.freeRecipesLimit')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-gray-700">{t('subscription.freeAILimit')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-gray-700">{t('subscription.freeFamily')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-gray-700">{t('subscription.calorieCalculation')}</span>
              </li>
            </ul>

            {isFree ? (
              <Button 
                disabled
                variant="outline"
                className="w-full py-6"
              >
                {t('subscription.currentPlanBadge')}
              </Button>
            ) : (
              <div className="text-center text-sm text-gray-500 py-6">
                {t('subscription.basicPlanAvailable')}
              </div>
            )}
          </Card>

          {/* Premium Plan */}
          <Card className="p-8 border-4 border-orange-400 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <Badge className="bg-orange-600 text-white px-3 py-1">
                <Sparkles className="w-3 h-3 mr-1" />
                {t('subscription.mostPopular')}
              </Badge>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-amber-900 mb-2 flex items-center justify-center gap-2">
                <Crown className="w-6 h-6 text-orange-600" />
                {t('subscription.premium')}
              </h3>
              <div className="text-4xl font-bold text-orange-600 mb-1">$4.99</div>
              <div className="text-gray-600">{t('subscription.perMonth')}</div>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.unlimitedRecipes')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.unlimitedAI')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.unlimitedFamilies')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.noAds')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.premiumImages')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.detailedNutrition')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.exportPDF')}</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-orange-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">{t('subscription.prioritySupport')}</span>
              </li>
            </ul>

            <Button 
              onClick={handleUpgrade}
              disabled={upgrading || isPremium}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white py-6 text-lg"
            >
              {upgrading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processando...
                </>
              ) : isPremium ? (
                '✓ Plano Ativo'
              ) : (
                'Começar Trial Gratuito'
              )}
            </Button>
            
            <p className="text-center text-sm text-gray-600 mt-4">
              💳 Apple Pay • Google Pay • Stripe • PayPal
            </p>
          </Card>

          {/* Family Plan */}
          <Card className="p-8 border-4 border-green-400 relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="absolute top-4 right-4">
              <Badge className="bg-green-600 text-white px-3 py-1">
                <Zap className="w-3 h-3 mr-1" />
                Melhor Valor
              </Badge>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-green-900 mb-2 flex items-center justify-center gap-2">
                👨‍👩‍👧‍👦 Familiar
              </h3>
              <div className="text-4xl font-bold text-green-600 mb-1">$19.99</div>
              <div className="text-gray-600">por mês</div>
              <Badge variant="secondary" className="mt-2 bg-green-100 text-green-700">
                Adicione 5 pessoas - Pague por 4!
              </Badge>
            </div>

            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏡</span>
                <span className="text-gray-700 font-semibold">Todos os benefícios do plano Premium</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏡</span>
                <span className="text-gray-700 font-semibold">5 contas, 5 pessoas, pague por 4</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏡</span>
                <span className="text-gray-700">Receitas compartilhadas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏡</span>
                <span className="text-gray-700">Planejador semanal em grupo</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">🏡</span>
                <span className="text-gray-700">IA ilimitada para todos</span>
              </li>
              <li className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="text-gray-700 font-semibold">Economia de $5/mês!</span>
              </li>
            </ul>

            <Button 
              onClick={() => handleUpgradeFamily()}
              disabled={upgrading}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 text-lg"
            >
              {upgrading ? t('subscription.processing') : t('subscription.subscribeFamily')}
            </Button>
            
            <p className="text-center text-sm text-green-700 mt-4 font-medium">
              💰 Economize $60 por ano comparado a 5 planos individuais!
            </p>
          </Card>
        </div>

        {/* FAQ */}
        <Card className="mt-12 p-8 max-w-4xl mx-auto border-2 border-amber-100">
          <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">Perguntas Frequentes</h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Posso cancelar a qualquer momento?</h4>
              <p className="text-gray-600">Sim! Cancele quando quiser sem taxas ou penalidades.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">O que acontece após o trial?</h4>
              <p className="text-gray-600">Você será cobrado $4.99/mês. Cancele antes se não quiser continuar.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Quais formas de pagamento aceitam?</h4>
              <p className="text-gray-600">Apple Pay, Google Pay, cartões de crédito/débito via Stripe, e PayPal.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Os dados são seguros?</h4>
              <p className="text-gray-600">Sim! Usamos Stripe, processador líder mundial em pagamentos seguros.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionPage;